class CartDiscountForm extends HTMLElement {
  constructor() {
    super();

    this.applyButton = this.querySelector('[data-apply-discount]');
    this.input = this.querySelector('input[name="discount"]');
    this.errorEl = this.querySelector('[data-discount-error]');

    if (this.applyButton) {
      this.applyButton.addEventListener('click', this.onApply.bind(this));
    }

    if (this.input) {
      this.input.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.onApply();
        }
      });
    }
  }

  getExistingCodes() {
    // Reads codes currently applied to the cart so we don't wipe out
    // other active discounts when adding a new one.
    return fetch(`${window.routes ? window.routes.cart_url : '/cart'}.js`)
      .then((res) => res.json())
      .then((cart) => {
        if (!cart.discount_codes) return [];
        return cart.discount_codes
          .filter((discount) => discount.applicable !== false)
          .map((discount) => discount.code);
      })
      .catch(() => []);
  }

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.classList.add('is-visible');
  }

  clearError() {
    if (!this.errorEl) return;
    this.errorEl.textContent = '';
    this.errorEl.classList.remove('is-visible');
  }

  setLoading(isLoading) {
    if (this.applyButton) {
      this.applyButton.disabled = isLoading;
      this.applyButton.classList.toggle('loading', isLoading);
    }
  }

  onApply(event) {
    if (event) event.preventDefault();
    const code = this.input ? this.input.value.trim() : '';

    if (!code) {
      this.showError('Please enter a discount code.');
      return;
    }

    this.clearError();
    this.setLoading(true);

    this.getExistingCodes().then((existingCodes) => {
      const codesToApply = existingCodes.includes(code) ? existingCodes : [...existingCodes, code];

      fetch(`${window.routes ? window.routes.cart_update_url : '/cart/update.js'}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: codesToApply.join(',') }),
      })
        .then((res) => res.json())
        .then((cart) => {
          const applied = (cart.discount_codes || []).find(
            (discount) => discount.code.toLowerCase() === code.toLowerCase()
          );

          if (!applied || applied.applicable === false) {
            const cartHasSubscriptionOnly =
              cart.items &&
              cart.items.length > 0 &&
              cart.items.every((item) => item.selling_plan_allocation);

            if (existingCodes.length > 0 || cartHasSubscriptionOnly) {
              // Either the cart already had an active, working discount
              // before this attempt, or every item in the cart is a
              // subscription line (which already carries its own discount
              // and can't combine with a code) — either way, the code isn't
              // needed here, not invalid.
              this.showError('Good news, this order already includes a discount.');
            } else {
              // No discount was active, and the cart has at least one
              // regular item the code could have applied to, so the code
              // itself is the problem: invalid, expired, or restricted.
              this.showError('That code isn\u2019t valid, or it doesn\u2019t apply to anything in your cart.');
            }
            this.setLoading(false);
            return;
          }

          this.refreshCartSections();
        })
        .catch(() => {
          this.showError('Something went wrong applying that code. Please try again.');
          this.setLoading(false);
        });
    });
  }

  onRemove(event) {
    if (event) event.preventDefault();
    this.setLoading(true);

    fetch(`${window.routes ? window.routes.cart_update_url : '/cart/update.js'}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: '' }),
    })
      .then((res) => res.json())
      .then(() => {
        this.refreshCartSections();
      })
      .catch(() => {
        this.showError('Something went wrong removing that code. Please try again.');
        this.setLoading(false);
      });
  }

  refreshCartSections() {
    const sectionId = this.dataset.sectionId;
    const isDrawer = this.closest('cart-drawer-items') !== null;
    const url = isDrawer
      ? `${window.routes ? window.routes.cart_url : '/cart'}?section_id=cart-drawer`
      : `${window.routes ? window.routes.cart_url : '/cart'}?section_id=${sectionId}`;

    fetch(url)
      .then((res) => res.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');

        if (isDrawer) {
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          selectors.forEach((selector) => {
            const target = document.querySelector(selector);
            const source = html.querySelector(selector);
            if (target && source) target.replaceWith(source);
          });
        } else {
          const target = document.getElementById('main-cart-footer');
          const source = html.querySelector('#main-cart-footer');
          if (target && source) target.replaceWith(source);

          // Cart items table also needs refreshing since the proportional
          // discount badges and crossed-out prices live there.
          const itemsTarget = document.getElementById('main-cart-items');
          const itemsSelector = itemsTarget ? itemsTarget.querySelector('.js-contents') : null;
          fetch(`${window.routes ? window.routes.cart_url : '/cart'}?section_id=main-cart-items`)
            .then((res) => res.text())
            .then((itemsHtml) => {
              const itemsParsed = new DOMParser().parseFromString(itemsHtml, 'text/html');
              const itemsSource = itemsParsed.querySelector('cart-items');
              const itemsContainer = document.querySelector('cart-items');
              if (itemsContainer && itemsSource) {
                itemsContainer.innerHTML = itemsSource.innerHTML;
              }
            });
        }

        // Cart icon bubble count
        fetch(`${window.routes ? window.routes.cart_url : '/cart'}?section_id=cart-icon-bubble`)
          .then((res) => res.text())
          .then((bubbleHtml) => {
            const bubbleParsed = new DOMParser().parseFromString(bubbleHtml, 'text/html');
            const bubbleSource = bubbleParsed.querySelector('.shopify-section');
            const bubbleTarget = document.getElementById('cart-icon-bubble');
            if (bubbleTarget && bubbleSource) bubbleTarget.innerHTML = bubbleSource.innerHTML;
          });
      })
      .catch(() => {
        this.showError('Something went wrong. Please refresh the page.');
        this.setLoading(false);
      });
  }
}

if (!customElements.get('cart-discount-form')) {
  customElements.define('cart-discount-form', CartDiscountForm);
}

// The "remove discount" x button lives inside the Subtotal block's discount
// line, which is a sibling of <cart-discount-form>, not a child of it. Since
// that markup also gets replaced wholesale on every AJAX cart refresh, a
// regular querySelector + addEventListener binding would silently die after
// the first refresh. A single delegated listener on the document survives
// any number of DOM swaps.
document.addEventListener('click', (event) => {
  const removeButton = event.target.closest('[data-remove-discount]');
  if (!removeButton) return;

  event.preventDefault();

  const formInstance = document.querySelector('cart-discount-form');
  if (formInstance && typeof formInstance.onRemove === 'function') {
    formInstance.onRemove(event);
  }
});